import { useMemo, useRef } from 'react';

import DatePickerInput from '@/components/modal/HistoricSearchModal/DatePickerInput';
import { ExtendedDatePicker } from '@/helpers/dateHelper';
import { getDateFormat } from '@/utils/format';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { format } from 'date-fns';
import { useLocale } from 'react-aria';
import { default as DatePicker } from 'react-datepicker';
import { MdRefresh } from 'react-icons/md';

export interface PortalOverviewControlsProps {
    isHistoric: boolean;
    setIsHistoric: (isHistoric: boolean) => void;
    startDate: Date;
    setStartDate: (date: Date) => void;
    setSummaryAge: (date: Date) => void;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalOverviewControls = (props: PortalOverviewControlsProps) => {
    const { isHistoric, setIsHistoric, startDate, setStartDate, setSummaryAge } = props;

    const lang = useLanguage();
    const { setSummaryHistoricDate, summaryApi, marginRequirementsApi, lastEODApi } = useSso();

    const dateRef = useRef<ExtendedDatePicker | null>(null);
    const { locale } = useLocale();
    const dateFormatPattern = getDateFormat(locale);

    const {
        data: lastEodData,
        isLoading: isLoadingLastEOD,
        error: errorLastEOD,
        isValidating: isValidatingLastEOD
    } = lastEODApi;

    const disableHistoricTab = useMemo(() => {
        return !!errorLastEOD || isLoadingLastEOD || isValidatingLastEOD || !lastEodData;
    }, [lastEODApi]);

    return (
        <div className="flex justify-between bg-brand-background-dark min-h-16 p-4 sm:p-6 py-4 border border-brand-neutral-700 text-brand-text rounded-md">
            <div className="flex flex-row items-center gap-3 sm:gap-6">
                <div
                    className={cn('cursor-pointer', {
                        'text-brand-primary': !isHistoric,
                        'hover:text-brand-primary-light': isHistoric
                    })}
                    onClick={() => {
                        setIsHistoric(false);
                        setSummaryHistoricDate(undefined);
                    }}>
                    {lang.portal.current}
                </div>
                <div
                    className={cn('cursor-pointer', {
                        'text-brand-primary': isHistoric && !disableHistoricTab,
                        'hover:text-brand-primary-light': !isHistoric && !disableHistoricTab,
                        'text-brand-neutral-400 cursor-not-allowed': disableHistoricTab
                    })}
                    onClick={() => {
                        if (!disableHistoricTab) {
                            setIsHistoric(true);
                            setSummaryHistoricDate(format(startDate, 'yyyy-MM-dd'));
                        }
                    }}>
                    {lang.portal.history}
                </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
                {isHistoric && lastEodData && (
                    <span>
                        <DatePicker
                            ref={dateRef}
                            selected={startDate}
                            dateFormat={dateFormatPattern}
                            onChange={(date) => {
                                if (date) {
                                    setStartDate(date);
                                    setSummaryHistoricDate(format(date, 'yyyy-MM-dd'));
                                    dateRef.current?.setOpen(false);
                                    summaryApi.mutate();
                                    marginRequirementsApi.mutate();
                                }
                            }}
                            disabled={!!errorLastEOD}
                            maxDate={new Date(lastEodData || '')}
                            customInput={
                                <DatePickerInput
                                    label={lang.portal.historicDate}
                                    parentRef={dateRef}
                                    labelClassName="!text-2xs"
                                    className="h-[30px] !rounded-md !bg-background shadow-lg !text-xs"
                                    inline
                                />
                            }
                            disabledKeyboardNavigation
                        />
                    </span>
                )}
                <div
                    className={cn('border border-brand-neutral-700 rounded-md p-1.5 cursor-pointer', {
                        'hover:bg-brand-primary': !summaryApi.isLoading && !summaryApi.isValidating
                    })}
                    onClick={() => {
                        if (!summaryApi.isLoading && !summaryApi.isValidating) {
                            summaryApi.mutate();
                            marginRequirementsApi.mutate();
                            setSummaryAge(new Date());
                        }
                    }}>
                    <MdRefresh
                        className={cn('w-4 h-4', {
                            'animate-spin text-brand-neutral-400': summaryApi.isLoading || summaryApi.isValidating
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default PortalOverviewControls;
